import { useEffect, useState } from 'react'
import { X, Send, User, Mail, Loader2, AlertCircle, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import api from '../services/api'

export interface ProposalContact {
  id: string
  email: string
  firstname: string
  lastname: string
  fullname: string
  phone: string
  poste: string
}

interface Props {
  proposalId: string
  proposalRef: string
  /** Portal customer ID — required when calling from admin pages */
  customerId?: number
  onClose: () => void
  /** Called after a successful send */
  onSuccess: () => void
}

export default function ProposalSignatureModal({
  proposalId,
  proposalRef,
  customerId,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation()

  const [loadingContacts, setLoadingContacts] = useState(true)
  const [contacts, setContacts] = useState<ProposalContact[]>([])
  const [contactsError, setContactsError] = useState('')
  const [selectedContact, setSelectedContact] = useState<ProposalContact | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const [sent, setSent] = useState(false)

  // Load ERP contacts for the proposal on mount
  useEffect(() => {
    api
      .getProposalContacts(proposalId, customerId)
      .then((data) => {
        setContacts(data)
        if (data.length === 0) {
          setContactsError(t('proposalSignature.noContacts'))
        } else if (data.length === 1) {
          setSelectedContact(data[0]) // Auto-select when only one contact
        }
      })
      .catch((err: unknown) => {
        const apiErr = err as { response?: { data?: { error?: string } } }
        setContactsError(apiErr?.response?.data?.error || t('proposalSignature.loadError'))
      })
      .finally(() => setLoadingContacts(false))
  }, [proposalId, customerId, t])

  const handleSend = async () => {
    if (!selectedContact) return
    setSending(true)
    setSendError('')
    try {
      await api.adminSendProposalForSignature(
        proposalId,
        customerId,
        selectedContact.email,
        selectedContact.firstname,
        selectedContact.lastname,
        i18n.language
      )
      setSent(true)
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 1500)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setSendError(apiErr?.response?.data?.error || t('proposalSignature.sendError'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('proposalSignature.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            {t('proposalSignature.subtitle', { ref: proposalRef })}
          </p>

          {/* Loading */}
          {loadingContacts && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
            </div>
          )}

          {/* No contacts / error */}
          {!loadingContacts && contactsError && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{contactsError}</p>
            </div>
          )}

          {/* Contact list */}
          {!loadingContacts && contacts.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {contacts.length === 1
                  ? t('proposalSignature.contactFound')
                  : t('proposalSignature.selectContact')}
              </p>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {contacts.map((c) => (
                  <button
                    key={c.id || c.email}
                    onClick={() => setSelectedContact(c)}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border-2 transition-colors ${
                      selectedContact?.email === c.email
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <User className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {c.fullname || c.email}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        {c.email}
                      </p>
                      {c.poste && <p className="text-xs text-gray-400 truncate">{c.poste}</p>}
                    </div>
                    {selectedContact?.email === c.email && (
                      <CheckCircle className="w-4 h-4 text-primary-500 flex-shrink-0 ml-auto mt-0.5" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Send error */}
          {sendError && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{sendError}</p>
            </div>
          )}

          {/* Success */}
          {sent && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700">{t('proposalSignature.sent')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSend}
            disabled={!selectedContact || sending || sent || !!contactsError}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('proposalSignature.send')}
          </button>
        </div>
      </div>
    </div>
  )
}
